/**
 * 将 client 端数据转换为底层数据
 */

import {
    StrokeDirectionType,
    type IAdvancedTextMaterial,
    type IFontGlow,
    type IFontShadow,
    type IFontStroke,
    type ITextLayerProps,
    type IVectorLayerProps,
} from '@/version-types';

export const transformLetterSpacingToApp = (letterSpacing: number, fontSize: number) => {
    return (letterSpacing / fontSize) * 1000;
};

export const transformLineSpacingToApp = (lineSpacing: number) => {
    return (lineSpacing - 1.2) * 1000;
};

export const transformGlowToApp = (textGlow: IFontGlow[], fontSize: number) => {
    return textGlow?.map((glow) => {
        return {
            color: glow.color,
            hidden: glow.hidden,
            width: ((glow.width / fontSize) * 512) / 25,
        };
    });
};

export const transformShadowToApp = (textShadow: IFontShadow[], fontSize: number) => {
    return textShadow?.map((shadow) => {
        return {
            color: shadow.color,
            hidden: shadow.hidden,
            x: shadow.x,
            y: shadow.y,
            blur: ((shadow.blur / fontSize) * 128) / 5,
        };
    });
};

export const transformStrokeToApp = (textStroke: IFontStroke[], fontSize: number) => {
    return textStroke?.map((stroke) => {
        return {
            color: stroke.color,
            hidden: stroke.hidden,
            direction: StrokeDirectionType.WITHOUT,
            width: ((stroke.width / fontSize) * 128.0) / 5.0,
        };
    });
};

export const transformArtTextDataToApp = (data: IAdvancedTextMaterial) => {
    const defaultLetterSpacing = transformLetterSpacingToApp(
        data.material.defaultLetterSpacing,
        data.material.defaultFontSize,
    );
    const textContents = data.textContents.map((item) => {
        const ret = { ...item };

        if (item.textStroke && item.textStroke.length > 0) {
            ret.textStroke = transformStrokeToApp(item.textStroke, data.material.defaultFontSize);
        }

        if (item.textShadow && item.textShadow.length > 0) {
            ret.textShadow = transformShadowToApp(item.textShadow, data.material.defaultFontSize);
        }

        if (item.textGlow && item.textGlow.length > 0) {
            ret.textGlow = transformGlowToApp(item.textGlow, data.material.defaultFontSize);
        }

        return ret;
    });

    return {
        id: data.id,
        material: { ...data.material, defaultLetterSpacing },
        textContents,
    };
};

export const transformAdvanceTextStyleToLowLever = (
    textGlow: IFontGlow[],
    textShadow: IFontShadow[],
    textStroke: IFontStroke[],
    fontSize: number,
) => {
    const result: {
        textGlow: IFontGlow[];
        textShadow: IFontShadow[];
        textStroke: IFontStroke[];
    } = {
        textGlow: [],
        textShadow: [],
        textStroke: [],
    };

    result.textGlow = transformGlowToApp(textGlow, fontSize);
    result.textShadow = transformShadowToApp(textShadow, fontSize);
    result.textStroke = transformStrokeToApp(textStroke, fontSize);

    return result;
};

export const transformTextDataToLowLever = (textLayerData: ITextLayerProps) => {
    const result = { ...textLayerData };
    const { fontSize, lineSpacing, letterSpacing, textGlow, textShadow, textStroke } = textLayerData;

    result.letterSpacing = transformLetterSpacingToApp(letterSpacing, fontSize);
    result.lineSpacing = transformLineSpacingToApp(lineSpacing);

    const advanceProps = transformAdvanceTextStyleToLowLever(textGlow, textShadow, textStroke, fontSize);

    if (textLayerData.advancedTextMaterial) {
        result.advancedTextMaterial = transformArtTextDataToApp(textLayerData.advancedTextMaterial);
    }

    return {
        ...result,
        ...advanceProps,
    };
};

export const transformClientVectorLayerDataToFoundation = (layerData: IVectorLayerProps) => {
    const { textInfo } = layerData;
    if (!textInfo) return layerData;

    const { fontSize, lineSpacing, letterSpacing } = textInfo;
    const nextTextInfo = {
        ...textInfo,
        lineSpacing: transformLineSpacingToApp(lineSpacing),
        letterSpacing: transformLetterSpacingToApp(letterSpacing, fontSize),
    };
    const nextLayerData: IVectorLayerProps = {
        ...layerData,
        textInfo: nextTextInfo,
    };

    return nextLayerData;
};
